// KDS Overview - Dashboard showing all stations at once
import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Beaker, 
  Scissors, 
  RefreshCw, 
  Package, 
  Truck,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

interface StationData {
  station: string;
  name: string;
  count: number;
  icon: string;
  color: string;
}

interface OverviewData {
  shop_name: string;
  stations: StationData[];
  total_active_orders: number;
  last_updated: string;
}

const KDSOverview: React.FC = () => {
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const stationIcons: Record<string, React.ReactNode> = {
    processing: <ClipboardList className="w-8 h-8" />,
    stain_removal: <Beaker className="w-8 h-8" />,
    alteration: <Scissors className="w-8 h-8" />,
    wash_dry: <RefreshCw className="w-8 h-8" />,
    fold_press: <RefreshCw className="w-8 h-8" />,
    pickup: <Package className="w-8 h-8" />,
    delivery: <Truck className="w-8 h-8" />
  };

  const fetchOverviewData = async () => {
    try {
      setError(null);
      
      // For development, try without auth first, then with auth
      let response = await fetch('/api/kds/overview');
      
      // If unauthorized, try with auth token
      if (response.status === 401) {
        response = await fetch('/api/kds/overview', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || 'dev-token'}`
          }
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setOverviewData(data.data);
    } catch (error) {
      console.error('Failed to fetch overview data:', error);
      setError('Failed to load KDS overview data - Make sure backend server is running');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
    
    // Refresh every 20 seconds (reduced frequency)
    const interval = setInterval(fetchOverviewData, 20000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-lg text-gray-600">Loading TDS Overview...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-800">Error Loading KDS Data</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button 
                onClick={fetchOverviewData}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStationStatus = (count: number) => {
    if (count === 0) return { status: 'idle', color: 'text-gray-400', bgColor: 'bg-gray-50' };
    if (count <= 3) return { status: 'normal', color: 'text-green-600', bgColor: 'bg-green-50' };
    if (count <= 6) return { status: 'busy', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    return { status: 'overloaded', color: 'text-red-600', bgColor: 'bg-red-50' };
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">TDS Overview Dashboard</h1>
        <div className="flex items-center space-x-4 text-gray-600">
          <span>Shop: {overviewData?.shop_name}</span>
          <span>•</span>
          <span>Total Active Orders: {overviewData?.total_active_orders || 0}</span>
          <span>•</span>
          <span>Last Updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{overviewData?.total_active_orders || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Stations</p>
              <p className="text-2xl font-bold text-gray-900">
                {overviewData?.stations.filter(s => s.count > 0).length || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Wait Time</p>
              <p className="text-2xl font-bold text-gray-900">12m</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <AlertCircle className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Urgent Orders</p>
              <p className="text-2xl font-bold text-gray-900">2</p>
            </div>
          </div>
        </div>
      </div>

      {/* Station Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {overviewData?.stations.map((station) => {
          const status = getStationStatus(station.count);
          const icon = stationIcons[station.station];
          
          return (
            <div
              key={station.station}
              className={`bg-white rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md ${
                station.count > 0 ? 'border-l-4' : 'border-gray-200'
              }`}
              style={{
                borderLeftColor: station.count > 0 ? station.color : undefined
              }}
            >
              <div className="p-6">
                {/* Station Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${station.color}20`, color: station.color }}
                    >
                      {icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{station.name}</h3>
                      <p className="text-sm text-gray-500">Department</p>
                    </div>
                  </div>
                </div>

                {/* Order Count */}
                <div className="mb-4">
                  <div className="flex items-baseline space-x-2">
                    <span className="text-3xl font-bold text-gray-900">{station.count}</span>
                    <span className="text-sm text-gray-500">orders</span>
                  </div>
                </div>

                {/* Status Indicator */}
                <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${status.bgColor}`}>
                  <div className={`w-2 h-2 rounded-full ${
                    status.status === 'idle' ? 'bg-gray-400' :
                    status.status === 'normal' ? 'bg-green-400' :
                    status.status === 'busy' ? 'bg-yellow-400' : 'bg-red-400'
                  }`}></div>
                  <span className={`text-sm font-medium ${status.color}`}>
                    {status.status === 'idle' ? 'Idle' :
                     status.status === 'normal' ? 'Normal' :
                     status.status === 'busy' ? 'Busy' : 'Overloaded'}
                  </span>
                </div>

                {/* Action Button */}
                <div className="mt-4">
                  <button
                    className="w-full py-2 px-4 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: station.color }}
                    onClick={() => window.location.href = `/kds/${station.station.replace('_', '-')}`}
                  >
                    View Station
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Workflow Flow Diagram */}
      <div className="mt-12 bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Workflow Flow</h2>
        <div className="flex items-center justify-between overflow-x-auto pb-4">
          {overviewData?.stations.map((station, index) => (
            <React.Fragment key={station.station}>
              <div className="flex flex-col items-center min-w-0 flex-shrink-0">
                <div 
                  className={`p-4 rounded-full mb-2 ${
                    station.count > 0 ? 'ring-2 ring-offset-2' : ''
                  }`}
                  style={{ 
                    backgroundColor: `${station.color}20`,
                    color: station.color,
                    '--tw-ring-color': station.count > 0 ? station.color : undefined
                  } as React.CSSProperties}
                >
                  {stationIcons[station.station]}
                </div>
                <span className="text-sm font-medium text-gray-900 text-center">{station.name}</span>
                <span className={`text-xs px-2 py-1 rounded-full mt-1 ${
                  station.count > 0 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                }`}>
                  {station.count} orders
                </span>
              </div>
              {index < overviewData.stations.length - 1 && (
                <div className="flex-shrink-0 mx-4">
                  <div className="w-8 h-0.5 bg-gray-300"></div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KDSOverview;
